import React, { useState, useEffect } from 'react';
import { useLazyQuery, useMutation, gql } from '@apollo/client';
import { toast } from 'react-toastify';
import { GENERATE_POST_DESCRIPTION } from '../../../../graphql/GENERATE_POST_DESCRIPTION';
import mascotHappy from '../../../../assets/svg/onboarding/mascot-happy.svg';

const GENERATE_PROJECT_TAGS = gql`
  query GenerateBrandTags($brandInfo: String!) {
    generateBrandTags(brandInfo: $brandInfo)
  }
`;

interface Tag {
    id: string;
    label: string;
}

interface ProjectIdeaStepProps {
    onComplete: (
        idea: string,
        useAI: boolean,
        aiGeneratedData?: any
    ) => void;
}

const ProjectIdeaStep: React.FC<ProjectIdeaStepProps> = ({ onComplete }) => {
    const [projectIdea, setProjectIdea] = useState('');
    const [availableTags, setAvailableTags] = useState<Tag[]>([]);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [isLoadingTags, setIsLoadingTags] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);

    const [generateTags] = useLazyQuery(GENERATE_PROJECT_TAGS, {
        onCompleted: (data) => {
            if (data?.generateBrandTags) {
                const tags = data.generateBrandTags.map((label: string) => ({
                    id: label.toLowerCase().replace(/\s+/g, '-'),
                    label
                }));
                setAvailableTags((prevTags) => {
                    const selectedTagObjects = prevTags.filter(tag => selectedTags.includes(tag.id));
                    const tagMap = new Map<string, Tag>();
                    selectedTagObjects.forEach(tag => tagMap.set(tag.id, tag));
                    tags.forEach(tag => tagMap.set(tag.id, tag));
                    return Array.from(tagMap.values());
                });
            }
            setIsLoadingTags(false);
        },
        onError: () => {
            setIsLoadingTags(false);
        }
    });

    const [generateDescription] = useMutation(GENERATE_POST_DESCRIPTION, {
        onCompleted: (data) => {
            if (data?.generatePostDescription?.description) {
                const generatedDesc = data.generatePostDescription.description;
                onComplete(
                    projectIdea,
                    true,
                    {
                        description: generatedDesc,
                        title: projectIdea.substring(0, 100)
                    }
                );
            }
            setIsGenerating(false);
        },
        onError: () => {
            toast.error('Failed to generate description. Please try again.');
            setIsGenerating(false);
        }
    });

    useEffect(() => {
        if (!projectIdea || projectIdea.trim().length < 3) {
            return;
        }

        const timer = setTimeout(() => {
            setIsLoadingTags(true);
            generateTags({ variables: { brandInfo: projectIdea } });
        }, 1000);

        return () => clearTimeout(timer);
    }, [projectIdea, generateTags]);

    useEffect(() => {
        setIsLoadingTags(true);
        generateTags({
            variables: {
                brandInfo: 'film production, creative project, content creation'
            }
        });
    }, [generateTags]);

    const toggleTag = (tagId: string) => {
        setSelectedTags((prev) =>
            prev.includes(tagId)
                ? prev.filter((id) => id !== tagId)
                : [...prev, tagId]
        );
    };

    const handleGenerateWithAI = async () => {
        if (!projectIdea.trim()) {
            toast.info('Please enter your project idea first');
            return;
        }

        setIsGenerating(true);

        await generateDescription({
            variables: {
                userInput: projectIdea,
                projectContext: {
                    title: '',
                    type: '',
                    locations: '',
                    collaborations: '',
                    fundingStatus: ''
                }
            }
        });
    };

    const handleFillManually = () => {
        onComplete(projectIdea, false);
    };

    return (
        <div className="columns section page-add-project-container no-bottom-margin">
            <div className="column">
                <div className="card page-add-project-card is-rounded is-dropshadowed project-idea-step">

                    <div className="card-content page-add-project-card-content">
                        <div className="has-text-centered mb-4">
                            <img
                                src={mascotHappy}
                                alt="Mascot"
                                className="project-mascot-image"
                            />
                        </div>
                        <h1 className="project-idea-heading">
                            A few sentences about your project
                        </h1>
                        <div className="field mb-5">
                            <textarea
                                className="textarea idea-info-textarea"
                                placeholder="I Need Videographers For My Film"
                                rows={1}
                                value={projectIdea}
                                onChange={(e) => setProjectIdea(e.target.value)}
                                disabled={isGenerating}
                                onInput={(e: React.FormEvent<HTMLTextAreaElement>) => {
                                    const target = e.currentTarget;
                                    target.style.height = 'auto';
                                    target.style.height = target.scrollHeight + 'px';
                                }}
                            />
                        </div>

                        <div className="idea-tags-section">
                            <div className="ai-tag-text">
                                <span>Add More With AI</span>
                            </div>

                            <div className="idea-tags-container">
                                {availableTags.map((tag) => (
                                    <button
                                        key={tag.id}
                                        type="button"
                                        className={`idea-tag ${selectedTags.includes(tag.id) ? 'selected' : ''}`}
                                        onClick={() => toggleTag(tag.id)}
                                        disabled={isLoadingTags || isGenerating}
                                    >
                                        <span className="tag-label">{tag.label}</span>
                                        <span className="tag-action-icon">
                                            {selectedTags.includes(tag.id) ? '×' : '+'}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="page-add-project-controls">
                        <div className="level container page-add-project-controls-container">
                            <div className="level-left page-add-project-controls-container-level" />
                            <div className="level-right page-add-project-controls-container-level">
                                <button
                                    type="button"
                                    className="button is-fullwidth-mobile page-add-project-controls-button has-text-weight-semibold is-outlined"
                                    onClick={handleFillManually}
                                    disabled={isGenerating}
                                >
                                    Fill Manually
                                </button>
                                <button
                                    type="button"
                                    className={`button is-fullwidth-mobile page-add-project-controls-button has-text-weight-semibold is-primary ${isGenerating ? 'is-loading' : ''}`}
                                    onClick={handleGenerateWithAI}
                                    disabled={isGenerating || !projectIdea.trim()}
                                >
                                    Generate with AI
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div >
    );
};

export default ProjectIdeaStep;